import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, X, Plus, Landmark } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const PRODUCT_TYPES = ["SFR", "Townhome", "Condo", "Multi-Family", "Mixed", "Other"];
const num = (v) => (v === "" || v == null ? null : Number(v));

function TagInput({ value = [], onChange, placeholder, testid }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setText("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input data-testid={testid} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} />
        <Button type="button" variant="outline" size="icon" onClick={add}><Plus className="h-4 w-4" /></Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted">
              {v}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== v))}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-sm">{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const Section = ({ value, title, children }) => (
  <AccordionItem value={value} className="border rounded-lg px-4 bg-card">
    <AccordionTrigger className="text-base font-medium hover:no-underline" data-testid={`bbb-section-${value}`}>{title}</AccordionTrigger>
    <AccordionContent className="pt-2 pb-4">{children}</AccordionContent>
  </AccordionItem>
);

const SwitchRow = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    <span className="text-sm">{label}</span>
  </label>
);

const EMPTY = {
  builder_name: "", contact_name: "", title: "", direct_email: "", direct_phone: "", division_phone: "",
  land_submission_url: "", counties_wanted: [], cities_wanted: [],
  raw_land_accepted: false, entitled_lots_accepted: false, finished_lots_accepted: false,
  min_acres: null, max_acres: null, min_lot_count: null, target_product_type: "",
  public_sewer_required: false, public_water_required: false, target_finished_lot_price: null,
  last_buy_box_confirmed: "", notes: "", active: true,
};

export default function BuilderBuyBoxEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(EMPTY);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/builder_buy_boxes/${id}`);
        setF((prev) => ({ ...prev, ...data,
          counties_wanted: Array.isArray(data.counties_wanted) ? data.counties_wanted : (data.counties_wanted ? String(data.counties_wanted).split(",").map((s) => s.trim()).filter(Boolean) : []),
          cities_wanted: Array.isArray(data.cities_wanted) ? data.cities_wanted : (data.cities_wanted ? String(data.cities_wanted).split(",").map((s) => s.trim()).filter(Boolean) : []),
        }));
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.builder_name.trim()) { toast.error("Builder Name is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/builder_buy_boxes/${id}`, f);
        toast.success("Builder saved");
      } else {
        const { data } = await api.post("/builder_buy_boxes", f);
        toast.success("Builder added");
        nav(`/builder-buy-box/${data.id}/edit`, { replace: true });
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto pb-24" data-testid="builder-buy-box-editor">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => nav("/builder-buy-box")}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-0.5"><Landmark className="h-3.5 w-3.5" /> Builder Buy Box</div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">{isEdit ? "Edit Builder" : "New Builder"}</h1>
        </div>
        <Button data-testid="save-builder-btn" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={["identity", "coverage", "criteria", "utils", "confirm"]} className="space-y-3">
        <Section value="identity" title="1. Builder & Contact">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Builder Name">
              <Input data-testid="bbb-builder_name" value={f.builder_name} onChange={(e) => set("builder_name", e.target.value)} placeholder="Lennar" />
            </Field>
            <div className="flex items-end"><SwitchRow label="Active" checked={f.active} onChange={(v) => set("active", v)} testid="bbb-active" /></div>
            <Field label="Contact Name"><Input data-testid="bbb-contact_name" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></Field>
            <Field label="Title"><Input data-testid="bbb-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Land Manager" /></Field>
            <Field label="Direct Email"><Input data-testid="bbb-direct_email" value={f.direct_email} onChange={(e) => set("direct_email", e.target.value)} placeholder="name@builder.com" /></Field>
            <Field label="Direct Phone"><Input data-testid="bbb-direct_phone" value={f.direct_phone} onChange={(e) => set("direct_phone", e.target.value)} placeholder="+1 (555) 123-4567" /></Field>
            <Field label="Division Phone"><Input data-testid="bbb-division_phone" value={f.division_phone} onChange={(e) => set("division_phone", e.target.value)} placeholder="+1 (555) 123-4567" /></Field>
            <Field label="Land Submission URL"><Input data-testid="bbb-land_submission_url" value={f.land_submission_url} onChange={(e) => set("land_submission_url", e.target.value)} placeholder="https://…" /></Field>
          </div>
        </Section>

        <Section value="coverage" title="2. Coverage">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Counties Wanted"><TagInput testid="bbb-counties_wanted" value={f.counties_wanted} onChange={(v) => set("counties_wanted", v)} placeholder="Add county" /></Field>
            <Field label="Cities Wanted"><TagInput testid="bbb-cities_wanted" value={f.cities_wanted} onChange={(v) => set("cities_wanted", v)} placeholder="Add city" /></Field>
          </div>
        </Section>

        <Section value="criteria" title="3. Land Criteria">
          <div className="flex flex-wrap gap-6 mb-4">
            <SwitchRow label="Raw Land Accepted" checked={f.raw_land_accepted} onChange={(v) => set("raw_land_accepted", v)} testid="bbb-raw_land_accepted" />
            <SwitchRow label="Entitled Lots Accepted" checked={f.entitled_lots_accepted} onChange={(v) => set("entitled_lots_accepted", v)} testid="bbb-entitled_lots_accepted" />
            <SwitchRow label="Finished Lots Accepted" checked={f.finished_lots_accepted} onChange={(v) => set("finished_lots_accepted", v)} testid="bbb-finished_lots_accepted" />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Min Acres"><Input type="number" data-testid="bbb-min_acres" value={f.min_acres ?? ""} onChange={(e) => set("min_acres", num(e.target.value))} /></Field>
            <Field label="Max Acres"><Input type="number" data-testid="bbb-max_acres" value={f.max_acres ?? ""} onChange={(e) => set("max_acres", num(e.target.value))} /></Field>
            <Field label="Min Lot Count"><Input type="number" data-testid="bbb-min_lot_count" value={f.min_lot_count ?? ""} onChange={(e) => set("min_lot_count", num(e.target.value))} /></Field>
            <Field label="Target Product Type">
              <Select value={f.target_product_type || ""} onValueChange={(v) => set("target_product_type", v)}>
                <SelectTrigger data-testid="bbb-target_product_type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{PRODUCT_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        </Section>

        <Section value="utils" title="4. Utilities & Pricing">
          <div className="flex flex-wrap gap-6 mb-4">
            <SwitchRow label="Public Sewer Required" checked={f.public_sewer_required} onChange={(v) => set("public_sewer_required", v)} testid="bbb-public_sewer_required" />
            <SwitchRow label="Public Water Required" checked={f.public_water_required} onChange={(v) => set("public_water_required", v)} testid="bbb-public_water_required" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Target Finished Lot Price ($)"><Input type="number" data-testid="bbb-target_finished_lot_price" value={f.target_finished_lot_price ?? ""} onChange={(e) => set("target_finished_lot_price", num(e.target.value))} /></Field>
          </div>
        </Section>

        <Section value="confirm" title="5. Confirmation & Notes">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Last Buy Box Confirmed"><Input type="date" data-testid="bbb-last_buy_box_confirmed" value={f.last_buy_box_confirmed || ""} onChange={(e) => set("last_buy_box_confirmed", e.target.value)} /></Field>
          </div>
          <div className="mt-4"><Field label="Notes"><Textarea data-testid="bbb-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={3} /></Field></div>
        </Section>
      </Accordion>
    </div>
  );
}
